"use strict";
// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
Object.defineProperty(exports, "__esModule", { value: true });
var algorithm_1 = require("@phosphor/algorithm");
var signaling_1 = require("@phosphor/signaling");
var coreutils_1 = require("@jupyterlab/coreutils");
var observables_1 = require("@jupyterlab/observables");
var rendermime_1 = require("@jupyterlab/rendermime");
/**
 * The default implementation of the IOutputAreaModel.
 */
var OutputAreaModel = /** @class */ (function () {
    /**
     * Construct a new observable outputs instance.
     */
    function OutputAreaModel(options) {
        if (options === void 0) { options = {}; }
        var _this = this;
        this.clearNext = false;
        this.list = null;
        this._trusted = false;
        this._isDisposed = false;
        this._stateChanged = new signaling_1.Signal(this);
        this._changed = new signaling_1.Signal(this);
        this._modelDB = null;
        this._serialized = null;
        this._changeGuard = false;
        this._trusted = !!options.trusted;
        this.contentFactory = (options.contentFactory ||
            OutputAreaModel.defaultContentFactory);
        this.list = new observables_1.ObservableList();
        if (options.values) {
            algorithm_1.each(options.values, function (value) { _this._add(value); });
        }
        this.list.changed.connect(this._onListChanged, this);
        // If we are given a IModelDB, keep an up-to-date
        // serialized copy of the OutputAreaModel in it.
        if (options.modelDB) {
            this._modelDB = options.modelDB;
            this._serialized = this._modelDB.createValue('outputs');
            if (this._serialized.get()) {
                this.fromJSON(this._serialized.get());
            }
            else {
                this._serialized.set(this.toJSON());
            }
            this._serialized.changed.connect(this._onSerializedChanged, this);
        }
    }
    Object.defineProperty(OutputAreaModel.prototype, "stateChanged", {
        /**
         * A signal emitted when the model state changes.
         */
        get: function () {
            return this._stateChanged;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(OutputAreaModel.prototype, "changed", {
        /**
         * A signal emitted when the model changes.
         */
        get: function () {
            return this._changed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(OutputAreaModel.prototype, "length", {
        /**
         * Get the length of the items in the model.
         */
        get: function () {
            return this.list ? this.list.length : 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(OutputAreaModel.prototype, "trusted", {
        /**
         * Get whether the model is trusted.
         */
        get: function () {
            return this._trusted;
        },
        /**
         * Set whether the model is trusted.
         *
         * #### Notes
         * Changing the value will cause all of the models to re-set.
         */
        set: function (value) {
            if (value === this._trusted) {
                return;
            }
            var trusted = this._trusted = value;
            for (var i = 0; i < this.list.length; i++) {
                var item = this.list.get(i);
                var value_1 = item.toJSON();
                item.dispose();
                item = this._createItem({ value: value_1, trusted: trusted });
                this.list.set(i, item);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(OutputAreaModel.prototype, "isDisposed", {
        /**
         * Test whether the model is disposed.
         */
        get: function () {
            return this._isDisposed;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Dispose of the resources used by the model.
     */
    OutputAreaModel.prototype.dispose = function () {
        if (this.isDisposed) {
            return;
        }
        this._isDisposed = true;
        this.list.dispose();
        signaling_1.Signal.clearData(this);
    };
    /**
     * Get an item at the specified index.
     */
    OutputAreaModel.prototype.get = function (index) {
        return this.list.get(index);
    };
    /**
     * Set the value at the specified index.
     */
    OutputAreaModel.prototype.set = function (index, value) {
        // Normalize stream data.
        this._normalize(value);
        var item = this._createItem({ value: value, trusted: this._trusted });
        this.list.set(index, item);
    };
    /**
     * Add an output, which may be combined with previous output.
     *
     * #### Notes
     * The output bundle is copied.
     * Contiguous stream outputs of the same `name` are combined.
     */
    OutputAreaModel.prototype.add = function (output) {
        // If we received a delayed clear message, then clear now.
        if (this.clearNext) {
            this.clear();
            this.clearNext = false;
        }
        return this._add(output);
    };
    /**
     * Clear all of the output.
     *
     * @param wait Delay clearing the output until the next message is added.
     */
    OutputAreaModel.prototype.clear = function (wait) {
        if (wait === void 0) { wait = false; }
        this._lastStream = '';
        if (wait) {
            this.clearNext = true;
            return;
        }
        algorithm_1.each(this.list, function (item) { item.dispose(); });
        this.list.clear();
    };
    /**
     * Deserialize the model from JSON.
     *
     * #### Notes
     * This will clear any existing data.
     */
    OutputAreaModel.prototype.fromJSON = function (values) {
        var _this = this;
        this.clear();
        algorithm_1.each(values, function (value) { _this._add(value); });
    };
    /**
     * Serialize the model to JSON.
     */
    OutputAreaModel.prototype.toJSON = function () {
        return algorithm_1.toArray(algorithm_1.map(this.list, function (output) { return output.toJSON(); }));
    };
    /**
     * Add an item to the list.
     */
    OutputAreaModel.prototype._add = function (value) {
        var trusted = this._trusted;
        // Normalize the value.
        this._normalize(value);
        // Consolidate outputs if they are stream outputs of the same kind.
        if (coreutils_1.nbformat.isStream(value) && this._lastStream &&
            value.name === this._lastName) {
            // In order to get a list change event, we add the previous
            // text to the current item and replace the previous item.
            // This also replaces the metadata of the last item.
            this._lastStream += value.text;
            value.text = this._lastStream;
            this._removeOverwrittenChars(value);
            var item_1 = this._createItem({ value: value, trusted: trusted });
            var index = this.length - 1;
            var prev = this.list.get(index);
            prev.dispose();
            this.list.set(index, item_1);
            return index;
        }
        if (coreutils_1.nbformat.isStream(value)) {
            this._removeOverwrittenChars(value);
        }
        // Create the new item.
        var item = this._createItem({ value: value, trusted: trusted });
        // Update the stream information.
        if (coreutils_1.nbformat.isStream(value)) {
            this._lastStream = value.text;
            this._lastName = value.name;
        }
        else {
            this._lastStream = '';
        }
        // Add the item to our list and return the new length.
        return this.list.push(item);
    };
    /**
     * Normalize an output.
     */
    OutputAreaModel.prototype._normalize = function (value) {
        if (coreutils_1.nbformat.isStream(value)) {
            if (Array.isArray(value.text)) {
                value.text = value.text.join('\n');
            }
        }
    };
    /**
     * Remove characters that are overridden by backspace characters.
     */
    OutputAreaModel.prototype._fixBackspace = function (txt) {
        var tmp = txt;
        do {
            txt = tmp;
            // Cancel out anything-but-newline followed by backspace
            tmp = txt.replace(/[^\n]\x08/gm, '');
        } while (tmp.length < txt.length);
        return txt;
    };
    /**
     * Remove chunks that should be overridden by the effect of
     * carriage return characters.
     */
    OutputAreaModel.prototype._fixCarriageReturn = function (txt) {
        var tmp = txt;
        // Handle multiple carriage returns before a newline
        tmp = tmp.replace(/\r\r+\n/gm, '\r\n');
        // Remove chunks that should be overridden by carriage returns
        do {
            // Remove any chunks preceding a carriage return unless carriage
            // return followed by a newline
            tmp = tmp.replace(/^[^\n]*(?:\r(?!\n))+/gm, '');
        } while (tmp.search(/\r(?!\n)/) > -1);
        do {
            // Replace remaining \r\n characters with a newline
            tmp = tmp.replace(/\r\n/gm, '\n');
        } while (tmp.indexOf('\r\n') > -1);
        return tmp;
    };
    /*
     * Remove characters overridden by backspaces and carriage returns
     */
    OutputAreaModel.prototype._removeOverwrittenChars = function (value) {
        var tmp = value.text;
        value.text = this._fixCarriageReturn(this._fixBackspace(tmp));
    };
    /**
     * Create an output item and hook up its signals.
     */
    OutputAreaModel.prototype._createItem = function (options) {
        var factory = this.contentFactory;
        var item = factory.createOutputModel(options);
        item.changed.connect(this._onGenericChange, this);
        item.changed.connect(this._onGenericChange, this);
        return item;
    };
    /**
     * Handle a change to the list.
     */
    OutputAreaModel.prototype._onListChanged = function (sender, args) {
        if (this._serialized && !this._changeGuard) {
            this._changeGuard = true;
            this._serialized.set(this.toJSON());
            this._changeGuard = false;
        }
        this._changed.emit(args);
        this._stateChanged.emit(void 0);
    };
    /**
     * If the serialized version of the outputs have changed due to a remote
     * action, then update the model accordingly.
     */
    OutputAreaModel.prototype._onSerializedChanged = function (sender, args) {
        if (!this._changeGuard) {
            this._changeGuard = true;
            this.fromJSON(args.newValue);
            this._changeGuard = false;
        }
    };
    /**
     * Handle a change to an item.
     */
    OutputAreaModel.prototype._onGenericChange = function () {
        this._stateChanged.emit(void 0);
    };
    return OutputAreaModel;
}());
exports.OutputAreaModel = OutputAreaModel;
/**
 * The namespace for OutputAreaModel class statics.
 */
(function (OutputAreaModel) {
    /**
     * The default implementation of a `IModelOutputFactory`.
     */
    var ContentFactory = /** @class */ (function () {
        function ContentFactory() {
        }
        /**
         * Create an output model.
         */
        ContentFactory.prototype.createOutputModel = function (options) {
            return new rendermime_1.OutputModel(options);
        };
        return ContentFactory;
    }());
    OutputAreaModel.ContentFactory = ContentFactory;
    /**
     * The default output model factory.
     */
    OutputAreaModel.defaultContentFactory = new ContentFactory();
})(OutputAreaModel = exports.OutputAreaModel || (exports.OutputAreaModel = {}));
exports.OutputAreaModel = OutputAreaModel;
